// frontend/src/pages/tools/ToolsLandingPage.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Stack,
  SimpleGrid,
  Paper,
  Box,
  Group,
  TextInput,
  Button,
  Alert,
} from '@mantine/core';
import {
  IconFlask,
  IconCalendarEvent,
  IconTestPipe2,
  IconMail,
  IconCheck,
  IconArrowRight,
} from '@tabler/icons-react';
import { trackEvent } from '@/utils/analytics';

const TOOLS = [
  {
    title: 'Trace Element Calculator',
    description:
      'Create custom trace element dosing batches with multiple containers. Export to PDF and share via URL.',
    path: '/tools/dosing-calculator',
    icon: IconFlask,
    color: 'blue',
  },
  {
    title: 'HTTM Scheduler',
    description:
      'Generate Hybrid Tank Transfer Method quarantine schedules. Remember when to change tanks and make sure you have all the right equipment.',
    path: '/tools/httm-scheduler',
    icon: IconCalendarEvent,
    color: 'teal',
  },
  {
    title: 'Dose Calculator',
    description:
      'Calculate required dosage to bring your tank up to target levels for major elements.',
    path: '/tools/dose-calculator',
    icon: IconTestPipe2,
    color: 'cyan',
  },
];

export function ToolsLandingPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      trackEvent('generate_lead', {
        item_name: 'email_signup',
        item_category: 'tools_landing',
      });

      const response = await fetch('/api/public/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: 'tools',
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Subscription failed');
      }

      setSubmitted(true);
      trackEvent('sign_up', {
        method: 'email',
        item_category: 'tools_landing',
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Stack gap="xs" ta="center">
          <Title order={2}>Free Reef Keeping Tools</Title>
          <Text c="dimmed" size="lg" maw={600} mx="auto">
            Calculators and planners to help you manage your reef tank. No
            signup required.
          </Text>
        </Stack>

        {/* Tool tiles */}
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {TOOLS.map((tool) => {
            const Icon = tool.icon;
            return (
              <Paper
                key={tool.path}
                component={Link}
                to={tool.path}
                withBorder
                p="lg"
                radius="md"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  transition:
                    'box-shadow 200ms ease, transform 200ms ease, border-color 200ms ease',
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  const el = e.currentTarget;
                  el.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  el.style.transform = 'translateY(-2px)';
                  el.style.borderColor = 'var(--mantine-color-teal-4)';
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  const el = e.currentTarget;
                  el.style.boxShadow = '';
                  el.style.transform = '';
                  el.style.borderColor = '';
                }}
                onClick={() => {
                  trackEvent('select_content', {
                    content_type: 'tool_tile',
                    item_id: tool.path,
                  });
                }}
              >
                <Stack gap="md">
                  <Box
                    style={{
                      background: `linear-gradient(135deg, var(--mantine-color-${tool.color}-6) 0%, var(--mantine-color-${tool.color}-4) 100%)`,
                      borderRadius: '50%',
                      width: 48,
                      height: 48,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={24} color="white" />
                  </Box>
                  <Stack gap="xs">
                    <Text fw={600} size="lg">
                      {tool.title}
                    </Text>
                    <Text size="sm" c="dimmed" lh={1.5}>
                      {tool.description}
                    </Text>
                  </Stack>
                  <Group gap="xs" mt="auto">
                    <Text size="sm" fw={500} c="teal">
                      Open Tool
                    </Text>
                    <IconArrowRight
                      size={14}
                      color="var(--mantine-color-teal-6)"
                    />
                  </Group>
                </Stack>
              </Paper>
            );
          })}
        </SimpleGrid>

        {/* Mailing list signup */}
        <Paper
          withBorder
          p="xl"
          radius="md"
          style={{
            background:
              'linear-gradient(135deg, var(--mantine-color-blue-6) 0%, var(--mantine-color-cyan-6) 100%)',
            color: 'white',
          }}
        >
          <Stack gap="md" maw={500} mx="auto" ta="center">
            <Title order={3} c="white">
              Stay in the Loop
            </Title>
            <Text size="sm" opacity={0.9}>
              Reef Data Hub is launching later this year. Track parameters, ICP
              results, dosing schedules, and more. Sign up to get notified when
              we launch and when new tools are released.
            </Text>

            {!submitted ? (
              <form onSubmit={handleSubmit}>
                <Group gap="xs" justify="center">
                  <TextInput
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    leftSection={<IconMail size={16} />}
                    error={error}
                    style={{ flex: 1, maxWidth: 300 }}
                    styles={{
                      input: {
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        color: 'var(--mantine-color-dark-7)',
                      },
                    }}
                    aria-label="Email address for launch notification"
                  />
                  <Button
                    type="submit"
                    variant="white"
                    color="blue"
                    loading={isSubmitting}
                  >
                    Notify Me
                  </Button>
                </Group>
              </form>
            ) : (
              <Alert
                icon={<IconCheck size={16} />}
                color="green"
                title="You're on the list!"
                radius="md"
              >
                Thanks for signing up. We'll let you know when we launch.
              </Alert>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
